const HYDRATION_CACHE = {}
const HYDRATION_SMALL_CACHE = {}
const HYDRATION_MEDIUM_CACHE = {}
const HYDRATION_QUIZ_CACHE = {}
const SKELETON_ATTRUBUTES = [
  'data-product-href',
  'data-product-image-primary',
  'data-product-image-secondary',
  'data-product-title',
  'data-product-description',
  'data-product-price',
]
class ProductCard extends HHBaseElement {
  constructor() {
    super()
    this._hydrated = !this.shouldHydrate
    this._selectItems = this._selectItems.bind(this)
    this._trackQuickShopToggle = this._trackQuickShopToggle.bind(this)
    this._notifyMeToggle = this._notifyMeToggle.bind(this)
    this._handleInCartToggle = this._handleInCartToggle.bind(this)
    this._handleVariantChange = this._handleVariantChange.bind(this)
    this._handleShadeClick = this._handleShadeClick.bind(this)
    this.variantSelected = false
    this._skeletonTemplate = document.getElementById('skeleton-product-card')
  }

  get productHandle() {
    return this.getAttribute('product-handle')
  }

  get sectionHeadline() {
    return this.getAttribute('data-section-headline')
  }

  get cardSize() {
    return this.getAttribute('card-size') ?? ''
  }

  get shouldHydrate() {
    return this.hasAttribute('hydrate')
  }

  get variantId() {
    return this.getAttribute('data-id') || ''
  }

  set variantId(value) {
    this.setAttribute('data-id', value)
  }

  static get observedAttributes() {
    return ['product-handle']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === 'product-handle' &&
      newValue !== oldValue &&
      newValue &&
      this.shouldHydrate
    ) {
      this._hydrated = false
      this._hydrate()
    }
  }

  connectedCallback() {
    if (this.shouldHydrate) {
      return this._hydrate()
    }
    this._selectItems()
    this._attachListeners()
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  async _hydrate(bypassCache = false) {
    const cacheVar =
      this.cardSize === 'small'
        ? HYDRATION_SMALL_CACHE
        : this.cardSize === 'medium'
          ? HYDRATION_MEDIUM_CACHE
          : this.cardSize === 'quiz'
            ? HYDRATION_QUIZ_CACHE
            : HYDRATION_CACHE
    const viewParam = this.cardSize
      ? `product-card-${this.cardSize}`
      : 'product-card'

    if (cacheVar[this.productHandle] && !bypassCache) {
      this._applyCacheEntry(cacheVar[this.productHandle])
    } else {
      if (!this.cardSize) this._renderSkeleton()
      let url = `${window.Shopify.routes.root}products/${this.productHandle}?view=${viewParam}`
      if (this.variantId) {
        url += `&variant=${this.variantId}`
      }
      const request = await fetch(url)
      if (!request.ok) return this.remove() // May return 404 if product is unavailable
      const container = document.createElement('div')
      container.innerHTML = await request.text()
      const productCardNode = container.querySelector(
        viewParam.replace('-quiz', '')
      )
      if (!productCardNode) return this.remove() // May return empty div if variant is unavailable
      cacheVar[this.productHandle] = {
        html: productCardNode.innerHTML,
        className: productCardNode.className,
      }
      this._applyCacheEntry(cacheVar[this.productHandle])
    }

    this._hydrated = true
    this._handleHydration()
  }

  _applyCacheEntry({ html, className }) {
    this.innerHTML = html
    className.split(/\s+/).forEach((name) => {
      if (name) this.classList.add(name)
    })
  }

  _renderSkeleton() {
    if (!this._skeletonTemplate) return null
    const contents = this._skeletonTemplate.cloneNode(true)
    this.innerHTML = contents.innerHTML
    SKELETON_ATTRUBUTES.forEach((attr) => {
      const data = this.getAttribute(attr)
      if (!!data) {
        const targets = Array.from(this.querySelectorAll(`[${attr}]`))
        targets.forEach((target) => {
          if (target.tagName === 'ADAPTIVE-IMAGE') {
            target.setAttribute('data-image', data)
          } else if (target.tagName === 'A') {
            target.setAttribute('href', data)
          } else {
            target.innerHTML = data
          }
        })
      }
    })
  }

  _handleHydration() {
    this._selectItems()
    this._removeListeners()
    this._attachListeners()
    this.classList.add('hydrated')
    this._setURLPostfix()
  }

  _setURLPostfix() {
    const urlPostfix = this.getAttribute('url-postfix')

    if (!!urlPostfix) {
      try {
        const link = this.querySelector('.js-product-card-link')
        if (link && !!link.href) {
          const href = new URL(link.href)
          href.searchParams.append(...urlPostfix.split('='))
          link.href = href.toString()
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  _selectItems() {
    this.quickShopToggle = this.querySelector('.js-toggle-quick-shop')
    this.notifyMeToggle = this.querySelector('.js-toggle-notify-me')
    this.inCartToggle = this.querySelector('.js-in-cart-toggle')
  }

  _handleShadeClick(e) {
    const swatch = e.target.closest(
      '[data-card-shade-selector] [data-variant-id]'
    )
    if (!swatch) return

    e.preventDefault()
    this._swapVariantParts(swatch.dataset.variantId)
  }

  // Shade row swap for the redesign card. Replaces only the media + details subtrees
  // rather than re-hydrating, and commits nothing until the fetch succeeds, so a failed
  // request leaves the previous variant intact and re-clickable.
  async _swapVariantParts(variantId) {
    if (!variantId || variantId === this.variantId) return

    // Only the most recent click may commit.
    const token = (this._swapToken = (this._swapToken || 0) + 1)

    const viewParam = this.cardSize
      ? `product-card-${this.cardSize}`
      : 'product-card'
    const cacheKey = `${this.productHandle}:${variantId}`
    this._variantPartsCache = this._variantPartsCache || {}

    let html = this._variantPartsCache[cacheKey]
    if (!html) {
      const url = `${window.Shopify.routes.root}products/${this.productHandle}?view=${viewParam}&variant=${variantId}`
      let request
      try {
        request = await fetch(url)
      } catch {
        return
      }
      if (!request.ok) return

      const container = document.createElement('div')
      container.innerHTML = await request.text()
      const freshCard = container.querySelector(viewParam.replace('-quiz', ''))
      if (!freshCard) return

      html = freshCard.innerHTML
      this._variantPartsCache[cacheKey] = html
    }

    // Bail if a newer click superseded this one while we were fetching.
    if (token !== this._swapToken) return

    this.variantId = variantId
    this.variantSelected = true

    const fresh = document.createElement('div')
    fresh.innerHTML = html
    ;[
      '.product-card-redesign__media',
      '.product-card-redesign__details',
      // Hidden swatch inputs live at the card root, not inside the two subtrees above,
      // so swap them too or notify-me reads the previous shade's hex/swatch.
      '.js-selected-variant-hex',
      '.js-selected-variant-swatch-image',
    ].forEach((selector) => {
      const from = fresh.querySelector(selector)
      const to = this.querySelector(selector)
      if (from && to) to.replaceWith(from)
    })

    // The old toggle buttons went out with the swapped subtree. Re-adding the delegated
    // listeners is a no-op — they're bound once in the constructor.
    this._selectItems()
    this._attachListeners()
    this._setURLPostfix()
  }

  _attachListeners() {
    this.addEventListener('click', this._handleShadeClick)

    if (this.quickShopToggle) {
      this.quickShopToggle.addEventListener('click', this._trackQuickShopToggle)
    }

    if (this.notifyMeToggle) {
      this.notifyMeToggle.addEventListener('click', this._notifyMeToggle)
    }

    if (this.inCartToggle) {
      this.inCartToggle.addEventListener('click', this._handleInCartToggle)
    }

    this.addEventListener('change', this._handleVariantChange)
  }

  _trackQuickShopToggle() {
    this.triggerEvent('click::quick-shop-toggle', {
      product_id: this.getAttribute('data-id'),
      variant_id: this.quickShopToggle.dataset.value,
    })
  }

  _notifyMeToggle() {
    const selectedVariantId = this.notifyMeToggle.dataset.variantId
    this.product = this.decodeProduct()
    const hexcode = this.querySelector('.js-selected-variant-hex')
    const swatchImageInput = this.querySelector(
      '.js-selected-variant-swatch-image'
    )

    let selectedItem = null
    this.product.variants.forEach((item) => {
      if (Number(selectedVariantId) === item.id) {
        selectedItem = item
      }
    })

    const params = {
      productId: this.product.id,
      variantId: selectedVariantId,
      // Classic nests the link inside the title, redesign nests the title inside the link;
      // read the title element itself so the selector holds for both.
      productTitle: this.querySelector(
        '.js-product-card-title'
      )?.textContent.trim(),
      sku: selectedItem?.sku,
      title: selectedItem?.title,
      image: selectedItem?.featured_image
        ? selectedItem?.featured_image.src
        : this.product.featured_image,
    }

    if (hexcode && hexcode?.value !== undefined) {
      params.hexCode = hexcode.value
    }
    if (swatchImageInput && swatchImageInput?.value) {
      params.swatchImage = swatchImageInput.value
    }
    this.fillNotifyMeModal(params)
  }

  fillNotifyMeModal({
    image,
    productTitle,
    hexCode,
    swatchImage,
    sku,
    productId,
    variantId,
    title,
  }) {
    const notifyMeImage = document.querySelector('.js-slideout-notify-me-image')
    const notifyMeTitle = document.querySelector('.js-slideout-notify-me-title')
    const notifyMeVariant = document.querySelector(
      '.js-slideout-notify-me-variant'
    )
    const notifyMeSku = document.querySelector('#slideout-notify-me-sku')
    const notifyMeProductId = document.querySelector(
      '#slideout-notify-me-product-id'
    )

    notifyMeImage.innerHTML = `<img src="${image}" alt="${productTitle}" />`
    notifyMeTitle.innerHTML = productTitle
    if (title.toLowerCase() === 'default title') {
      notifyMeVariant.innerHTML = ''
    } else {
      let swatchHTML = ''

      // Prioritize swatch image over hex codes
      if (swatchImage) {
        swatchHTML = `<span class="block" style="background-image: url(${swatchImage}); background-size: cover;"></span>`
      } else if (hexCode) {
        swatchHTML = `<span class="block" style="background-color: ${hexCode};"></span>`
      }

      notifyMeVariant.innerHTML = `${swatchHTML}${title}`
    }
    notifyMeSku.value = sku
    notifyMeProductId.value = productId
    const notifyMeVariantId = document.querySelector(
      '#slideout-notify-me-variant-id'
    )
    if (notifyMeVariantId) notifyMeVariantId.value = variantId
  }

  decodeProduct(id) {
    return JSON.parse(
      this.querySelector(`#card-product-data-${id}`).textContent
    )
  }

  _removeListeners() {
    this.removeEventListener('click', this._handleShadeClick)

    if (this.quickShopToggle) {
      this.quickShopToggle.removeEventListener(
        'click',
        this._trackQuickShopToggle
      )
    }

    if (this.notifyMeToggle) {
      this.notifyMeToggle.removeEventListener('click', this._notifyMeToggle)
    }

    if (this.inCartToggle) {
      this.inCartToggle.removeEventListener('click', this._handleInCartToggle)
    }

    this.removeEventListener('change', this._handleVariantChange)
  }

  _handleVariantChange(event) {
    const variantId = event.target.dataset.variantId
    const productHandle = event.target.dataset.productHandle

    if (variantId) {
      if (productHandle && productHandle !== this.productHandle) {
        this.productHandle = productHandle
      }
      this.variantId = variantId
      this._hydrate(true)
      this.variantSelected = true
    }
  }

  async _handleInCartToggle(e) {
    try {
      e.preventDefault()
      this.classList.add('busy')
      const cartLineItemKey = e.currentTarget.dataset.lineItemKey
      const cartItemsToRemove = (e.currentTarget.dataset.itemsToRemove || '')
        .split(',')
        .filter(Boolean)
      const property = this.inCartToggle.dataset.lineProperty
      if (cartItemsToRemove.length) {
        await window.__CART__CONTROLLER__.update(
          cartItemsToRemove.reduce((acc, item) => {
            acc[item] = 0
            return acc
          }, {})
        )
      }
      if (cartLineItemKey) {
        await window.__CART__CONTROLLER__.update({
          [cartLineItemKey]: 0,
        })
      } else {
        await window.__CART__CONTROLLER__.add([
          {
            id: this.inCartToggle.dataset.value,
            quantity: 1,
            ...(property ? { properties: { [property]: true } } : {}),
          },
        ])

        this.triggerEvent('click::add-to-cart', {
          product_id: this.dataset.id,
          variant_id: this.inCartToggle.dataset.value,
        })
      }

      this.triggerGlobalEvent('main-cart::refresh')
    } catch (error) {
      console.error(error)
      this.classList.remove('busy')
    }
  }
}
window.customElements.define('product-card', ProductCard)
